import { jwtDecode } from "jwt-decode";

import { READ_CACHE_NAMES } from "~/helpers/sw/readCacheAllowlist";
import buildReadCacheKey, {
	type ReadCacheScope,
	readOperationNameFromCacheKey,
	readTenantIdFromCacheKey
} from "~/helpers/sw/readCacheKey";

const TOKEN_STORAGE_KEY = "appTokens";

export interface CurrentScopeReadCacheKeyInput {
	operationName: string | undefined;
	variables?: unknown;
}

export interface ReadCachePresenceInput<TGroup extends string = string> {
	cacheStorage: Pick<CacheStorage, "keys" | "open"> | undefined;
	operationNamesByGroup: Readonly<Record<TGroup, readonly string[]>>;
	tenantId: string | undefined;
}

const readAccessToken = (): string | undefined => {
	const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
	if (!stored) return undefined;
	try {
		const tokens = JSON.parse(stored) as Partial<TokenSet>;
		return tokens.accessToken || undefined;
	} catch {
		return undefined;
	}
};

const resolveReadCacheScope = (): ReadCacheScope | undefined => {
	const accessToken = readAccessToken();
	if (!accessToken) return undefined;
	try {
		const { sub, wsTenantId } = jwtDecode<SFJWT>(accessToken);
		return wsTenantId && sub ? { tenantId: wsTenantId, userId: sub } : undefined;
	} catch {
		return undefined;
	}
};

export const buildCurrentScopeReadCacheKey = ({
	operationName,
	variables
}: CurrentScopeReadCacheKeyInput): string | undefined => {
	const scope = resolveReadCacheScope();
	return scope ? buildReadCacheKey({ operationName, scope, variables }) : undefined;
};

export const hasReadCacheEntriesFor = async <TGroup extends string>({
	cacheStorage,
	operationNamesByGroup,
	tenantId
}: ReadCachePresenceInput<TGroup>): Promise<Partial<Record<TGroup, boolean>> | undefined> => {
	const activeGroups = (Object.entries(operationNamesByGroup) as Array<[TGroup, readonly string[]]>).filter(
		([, operationNames]) => operationNames.length > 0
	);
	if (!cacheStorage || !tenantId || activeGroups.length === 0) return undefined;

	const presenceByGroup = Object.fromEntries(activeGroups.map(([group]) => [group, false])) as Partial<
		Record<TGroup, boolean>
	>;

	try {
		const existingNames = await cacheStorage.keys();
		for (const cacheName of READ_CACHE_NAMES) {
			if (!existingNames.includes(cacheName)) continue;
			const cache = await cacheStorage.open(cacheName);
			for (const request of await cache.keys()) {
				const path = new URL(request.url).pathname;
				if (readTenantIdFromCacheKey(path) !== tenantId) continue;
				const operationName = readOperationNameFromCacheKey(path);
				if (!operationName) continue;
				for (const [group, operationNames] of activeGroups) {
					if (operationNames.includes(operationName)) presenceByGroup[group] = true;
				}
			}
		}
		return presenceByGroup;
	} catch {
		return undefined;
	}
};

export default resolveReadCacheScope;
